import { Box, Skeleton, Typography } from "@mui/material";
import { BORDER_RADIUS, COLORS } from "app/theme";
import { transformMapTileRequest } from "common/utils/mapTransformRequest";
import { ONLINE_MAP_STYLE } from "features/mapLayers/offlineMapStyle";
import type { FeatureCollection } from "geojson";
import { useInsightsScope } from "model/InsightsScopeContext";
import { useAccessibleFarmerProgrammes } from "model/useDashboardQueries";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { MapRef } from "react-map-gl/mapbox";
import {
  AttributionControl,
  Layer,
  Map as MapGL,
  Source,
} from "react-map-gl/mapbox";
import { buildFarmerRows } from "../corporate/FarmerTable";
import {
  usePerFarmerActions,
  useProgrammeFarmLocations,
} from "../hooks/useInsightsDashboardQueries";

const MAP_HEIGHT = 340;

// Marker colour by farmer journey stage — matches the Farmer Journey cards.
const STATUS_DOT: Record<string, string> = {
  actioning: COLORS.dahlia[700],
  onboarding: COLORS.bellflower[700],
};
const STATUS_HALO: Record<string, string> = {
  actioning: COLORS.dahlia[500],
  onboarding: COLORS.bellflower[500],
};
const UNKNOWN_DOT = "#9ca3af";
const UNKNOWN_HALO = "#9ca3af";

// Sensible default while bounds are being computed (centred on the UK).
const DEFAULT_VIEWPORT = { longitude: -2.5, latitude: 53.4, zoom: 4.5 };

type Point = {
  lng: number;
  lat: number;
  name: string;
  guid: string;
  status: string;
};

type Bounds = [[number, number], [number, number]];

const MapShell = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      position: "relative",
      width: "100%",
      height: MAP_HEIGHT,
      borderRadius: `${BORDER_RADIUS.medium}px`,
      overflow: "hidden",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
      backgroundColor: COLORS.dahlia[50],
      // Keep the bottom-left corner clear for the caption; attribution sits
      // bottom-right.
      "& .mapboxgl-ctrl-logo": { display: "none" },
    }}
  >
    {children}
  </Box>
);

export default function ProgrammeFarmMap({
  programmeGuid,
}: {
  programmeGuid?: string | null;
}) {
  const scope = useInsightsScope();
  const { data, isLoading } = useProgrammeFarmLocations(
    programmeGuid ?? undefined
  );
  const { data: farmersData } = useAccessibleFarmerProgrammes();
  const { data: actionsData } = usePerFarmerActions();
  const mapRef = useRef<MapRef | null>(null);
  const [loaded, setLoaded] = useState(false);

  // Farmer journey status keyed by farmer guid (same logic as the funnel).
  const statusByGuid = useMemo<Map<string, string>>(() => {
    const rows = buildFarmerRows(
      farmersData ?? [],
      actionsData?.data,
      undefined,
      undefined,
      null
    );
    const map = new Map<string, string>();
    for (const r of rows) map.set(r.guid, r.status);
    return map;
  }, [farmersData, actionsData?.data]);

  const points = useMemo<Point[]>(() => {
    return (data?.data ?? [])
      .map((r) => {
        const guid = r["ProgrammeFarmLocations.guid"];
        return {
          lng: Number(r["ProgrammeFarmLocations.centroidLng"]),
          lat: Number(r["ProgrammeFarmLocations.centroidLat"]),
          name: r["ProgrammeFarmLocations.name"],
          guid,
          status: statusByGuid.get(guid) ?? "unknown",
        };
      })
      .filter(
        (p) =>
          Number.isFinite(p.lng) &&
          Number.isFinite(p.lat) &&
          p.lng !== 0 &&
          p.lat !== 0
      );
  }, [data?.data, statusByGuid]);

  const geojson = useMemo<FeatureCollection>(
    () => ({
      type: "FeatureCollection",
      features: points.map((p) => ({
        type: "Feature",
        geometry: { type: "Point", coordinates: [p.lng, p.lat] },
        properties: { name: p.name, guid: p.guid, status: p.status },
      })),
    }),
    [points]
  );

  const bounds = useMemo<Bounds | null>(() => {
    if (points.length === 0) return null;
    const lngs = points.map((p) => p.lng);
    const lats = points.map((p) => p.lat);
    return [
      [Math.min(...lngs), Math.min(...lats)],
      [Math.max(...lngs), Math.max(...lats)],
    ];
  }, [points]);

  // Fit the camera to the bounding box of all farm dots, with padding.
  const fitToBounds = useCallback(() => {
    const map = mapRef.current;
    if (!map || !bounds) return;
    map.resize();
    map.fitBounds(bounds, {
      padding: { top: 32, bottom: 48, left: 32, right: 32 },
      maxZoom: 12,
      duration: 0,
    });
  }, [bounds]);

  useEffect(() => {
    if (loaded) fitToBounds();
  }, [loaded, fitToBounds]);

  if (isLoading) {
    return (
      <Skeleton
        variant="rounded"
        height={MAP_HEIGHT}
        sx={{ borderRadius: `${BORDER_RADIUS.medium}px` }}
      />
    );
  }

  if (points.length === 0)
    return scope && scope.intakeYear !== "all" ? (
      <Typography variant="body2" color="text.secondary">
        No mapped farms for this cohort.
      </Typography>
    ) : null;

  return (
    <MapShell>
      <Box sx={{ position: "absolute", inset: 0 }}>
        <MapGL
          ref={mapRef}
          onLoad={() => {
            setLoaded(true);
            fitToBounds();
          }}
          initialViewState={DEFAULT_VIEWPORT}
          mapboxAccessToken={import.meta.env.VITE_MAPBOX_ACCESS_TOKEN}
          mapStyle={ONLINE_MAP_STYLE}
          dragPan={false}
          dragRotate={false}
          scrollZoom={false}
          doubleClickZoom={false}
          touchZoomRotate={false}
          keyboard={false}
          maxPitch={0}
          pitchWithRotate={false}
          transformRequest={(url: string) => transformMapTileRequest(url)}
          attributionControl={false}
        >
          <AttributionControl position="bottom-right" compact />
          <Source id="programme-farms" type="geojson" data={geojson}>
            <Layer
              id="programme-farms-halo"
              type="circle"
              paint={{
                "circle-radius": 11,
                "circle-color": [
                  "match",
                  ["get", "status"],
                  "actioning",
                  STATUS_HALO.actioning,
                  "onboarding",
                  STATUS_HALO.onboarding,
                  UNKNOWN_HALO,
                ],
                "circle-opacity": 0.2,
              }}
            />
            <Layer
              id="programme-farms-dot"
              type="circle"
              paint={{
                "circle-radius": 6,
                "circle-color": [
                  "match",
                  ["get", "status"],
                  "actioning",
                  STATUS_DOT.actioning,
                  "onboarding",
                  STATUS_DOT.onboarding,
                  UNKNOWN_DOT,
                ],
                "circle-stroke-color": "#ffffff",
                "circle-stroke-width": 2,
              }}
            />
          </Source>
        </MapGL>
      </Box>
      <Box
        sx={{
          position: "absolute",
          left: 12,
          bottom: 12,
          px: 1.25,
          py: 0.5,
          borderRadius: `${BORDER_RADIUS.small}px`,
          backgroundColor: "rgba(255,255,255,0.9)",
          backdropFilter: "blur(2px)",
          pointerEvents: "none",
        }}
      >
        <Typography
          variant="caption"
          sx={{ color: "#3f3f46", fontWeight: 600 }}
        >
          Approximate locations · {points.length} farm
          {points.length === 1 ? "" : "s"}
        </Typography>
      </Box>
    </MapShell>
  );
}
